import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, AlertTriangle, Trophy, ArrowLeft } from "lucide-react";
import api from "../../lib/api";
import { ymd, meseLabel, last12Months, pctColor, DOW_ABBR, fmtGiornoLong } from "./helpers";
import { Button } from "../../components/ui/button";
import PulizieRegistro from "./PulizieRegistro";

export default function PulizieDashboard({ structureId, strutturaNome, companyLogo }) {
  const [mese, setMese] = useState(new Date().toISOString().slice(0, 7));
  const [dayView, setDayView] = useState(null);
  const months = last12Months();
  const { data, isLoading } = useQuery({
    queryKey: ["pul-mese", structureId, mese],
    queryFn: async () => (await api.get("/pulizie/mese", { params: { structure_id: structureId, mese } })).data,
  });
  const giorni = data?.giorni || [];
  const classifica = data?.classifica || [];
  const tot = data?.totali || {};
  const todayIso = ymd(new Date());
  const MEDAGLIE = ["#eab308", "#94a3b8", "#b45309"];

  if (dayView) {
    return (
      <div data-testid="pul-dashboard-day">
        <Button variant="outline" size="sm" onClick={() => setDayView(null)} data-testid="pul-day-back" className="mb-4 font-semibold"><ArrowLeft size={15} className="mr-1" /> Torna alla dashboard</Button>
        <h2 className="font-head font-bold text-lg mb-3 capitalize">{fmtGiornoLong(dayView)}</h2>
        <PulizieRegistro structureId={structureId} data={dayView} setData={setDayView} strutturaNome={strutturaNome} companyLogo={companyLogo} />
      </div>
    );
  }

  return (
    <div data-testid="pul-dashboard">
      {/* Lista mesi */}
      <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
        {months.map((m) => (
          <button key={m} onClick={() => setMese(m)} data-testid={`pul-mese-${m}`} className={`px-3.5 py-2 rounded-xl text-sm font-semibold whitespace-nowrap shrink-0 border transition-colors ${mese === m ? "bg-sky-600 text-white border-sky-600" : "bg-white hover:bg-slate-50"}`}>
            <div className="capitalize">{meseLabel(m)}</div>
          </button>
        ))}
      </div>

      {/* KPI mese */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
        <div className="bg-white rounded-xl border p-4" data-testid="pul-kpi-pct">
          <div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Completamento</div>
          <div className={`text-3xl font-head font-bold ${pctColor(tot.pct || 0, false).text}`}>{tot.pct || 0}%</div>
          <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className={`h-full ${pctColor(tot.pct || 0, false).bar}`} style={{ width: `${tot.pct || 0}%` }} /></div>
        </div>
        <div className="bg-white rounded-xl border p-4" data-testid="pul-kpi-fatte"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Fatte</div><div className="text-3xl font-head font-bold text-emerald-700">{tot.fatte || 0}</div><div className="text-[11px] text-muted-foreground">su {tot.totale || 0} previste</div></div>
        <div className="bg-white rounded-xl border p-4" data-testid="pul-kpi-rivedere"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Da rivedere</div><div className="text-3xl font-head font-bold text-amber-600">{tot.da_rivedere || 0}</div></div>
        <div className="bg-white rounded-xl border p-4" data-testid="pul-kpi-arretrate"><div className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">Arretrate</div><div className="text-3xl font-head font-bold text-red-600">{tot.arretrate || 0}</div></div>
      </div>

      {isLoading ? <p className="text-muted-foreground">Caricamento…</p> : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Griglia giorni */}
          <div className="lg:col-span-2">
            <h2 className="font-head font-bold text-base mb-3 flex items-center gap-2"><BarChart3 size={16} className="text-sky-600" /> Giorni di <span className="capitalize">{meseLabel(mese)}</span></h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
              {giorni.map((g) => {
                const col = pctColor(g.pct, g.futuro);
                const vuoto = g.totale === 0;
                return (
                  <button key={g.data} onClick={() => !vuoto && setDayView(g.data)} disabled={vuoto} data-testid={`pul-day-card-${g.data}`}
                    className={`text-left bg-white rounded-xl border p-2.5 transition-shadow ${vuoto ? "opacity-50 cursor-default" : "hover:shadow-md cursor-pointer"} ${g.data === todayIso ? "ring-2 ring-sky-400" : ""} ${col.ring}`}>
                    <div className="flex items-baseline justify-between">
                      <div className="font-head font-bold text-lg leading-none">{g.giorno}<span className="text-[10px] font-semibold text-muted-foreground ml-1">{DOW_ABBR[g.dow]}</span></div>
                      {!vuoto && <span className={`text-xs font-bold ${col.text}`}>{g.pct}%</span>}
                    </div>
                    {vuoto ? <div className="text-[10px] text-muted-foreground mt-2">Nessuna pulizia</div> : (
                      <>
                        <div className="h-1.5 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className={`h-full ${col.bar}`} style={{ width: `${g.pct}%` }} /></div>
                        <div className="text-[11px] text-muted-foreground mt-1.5">{g.fatte}/{g.totale} fatte{g.futuro ? " (prev.)" : ""}</div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {g.da_rivedere > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{g.da_rivedere} rivedi</span>}
                          {g.arretrate > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">{g.arretrate} arretr.</span>}
                          {g.verifiche_da_sistemare > 0 && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 flex items-center gap-0.5"><AlertTriangle size={9} />{g.verifiche_da_sistemare}</span>}
                        </div>
                        {g.operatori.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`pul-day-ops-${g.data}`}>{g.operatori.slice(0, 3).map((o) => <span key={o.nome} className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 truncate max-w-[90px]" title={`${o.nome}: ${o.count}`}>{o.nome.split(" ")[0]}</span>)}{g.operatori.length > 3 && <span className="text-[9px] text-muted-foreground">+{g.operatori.length - 3}</span>}</div>}
                        {g.mancanti.length > 0 && <div className="mt-1 flex flex-wrap gap-1" data-testid={`pul-day-manc-${g.data}`}>{g.mancanti.slice(0, 2).map((o) => <span key={o.nome} className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 truncate max-w-[90px]" title={`Non fatto: ${o.nome} (${o.count})`}>✗ {o.nome.split(" ")[0]}</span>)}{g.mancanti.length > 2 && <span className="text-[9px] text-muted-foreground">+{g.mancanti.length - 2}</span>}</div>}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Classifica operatori */}
          <div data-testid="pul-classifica">
            <h2 className="font-head font-bold text-base mb-3 flex items-center gap-2"><Trophy size={16} className="text-amber-500" /> Classifica operatori</h2>
            {classifica.length === 0 ? (
              <div className="bg-white rounded-xl border p-6 text-center text-sm text-muted-foreground">Nessuna pulizia registrata questo mese.</div>
            ) : (
              <div className="bg-white rounded-xl border divide-y">
                {classifica.map((c, i) => (
                  <div key={c.nome} className="flex items-center gap-3 p-3" data-testid={`pul-classifica-row-${i}`}>
                    <div className="w-7 h-7 rounded-full grid place-items-center text-white text-xs font-bold shrink-0" style={{ background: MEDAGLIE[i] || "#cbd5e1" }}>{i < 3 ? "🏅" : i + 1}</div>
                    <div className="flex-1 min-w-0"><div className="font-semibold text-sm truncate">{c.nome}</div>{c.da_rivedere > 0 && <div className="text-[11px] text-amber-600">{c.da_rivedere} da rivedere</div>}</div>
                    <div className="text-right"><div className="font-head font-bold text-lg text-emerald-700">{c.fatte}</div><div className="text-[10px] text-muted-foreground -mt-1">pulizie</div></div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-3">Clicca un giorno per aprire il dettaglio completo qui nella dashboard.</p>
          </div>
        </div>
      )}
    </div>
  );
}
